import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { BrandingFont } from '@/types/branding.types';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { Combobox } from '@/components/ui/Combobox';
import { Checkbox } from '@/components/ui/Checkbox';
import { devLog } from '@/utils/logger';
import { apiClient } from '@/services/api';

/**
 * TypographySettings Component
 *
 * Manages custom font selection from Google Fonts
 */

interface TypographySettingsProps {
  font?: BrandingFont;
  headlineFont?: BrandingFont;
  onChange: (font: BrandingFont | undefined) => void;
  onHeadlineFontChange: (font: BrandingFont | undefined) => void;
  /** Resolved fonts shown when the scope has no own font (Brand Portfolio value or default) */
  inheritedFont?: BrandingFont;
  inheritedHeadlineFont?: BrandingFont;
  /** 'portfolio-brand' = a brand inside a Brand Portfolio; 'standalone' = agency or standalone team */
  scope?: 'portfolio-brand' | 'standalone';
}

// Fallback fonts if API is unavailable
const FALLBACK_FONTS = [
  { value: 'Inter', label: 'Inter' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Open Sans', label: 'Open Sans' },
  { value: 'Lato', label: 'Lato' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Raleway', label: 'Raleway' },
  { value: 'Nunito', label: 'Nunito' },
  { value: 'Work Sans', label: 'Work Sans' },
  { value: 'DM Sans', label: 'DM Sans' },
  { value: 'Manrope', label: 'Manrope' },
  { value: 'Plus Jakarta Sans', label: 'Plus Jakarta Sans' },
  { value: 'Space Grotesk', label: 'Space Grotesk' },
  { value: 'Outfit', label: 'Outfit' },
  { value: 'Sora', label: 'Sora' },
  { value: 'Lexend', label: 'Lexend' },
  { value: 'Urbanist', label: 'Urbanist' },
  { value: 'Source Sans 3', label: 'Source Sans 3' },
  { value: 'Rubik', label: 'Rubik' },
  { value: 'Karla', label: 'Karla' },
];

const DEFAULT_FONT = {
  family: 'DM Sans',
  url: 'https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600;1,700&display=swap',
  weights: ['400', '500', '600', '700'],
  italic: true,
} as const;

const getDefaultWeights = (): string[] => ['400', '500', '600', '700'];
const getDefaultItalic = (): boolean => true;

const sameFont = (a: BrandingFont | undefined, b: BrandingFont | undefined): boolean =>
  !!a && !!b && JSON.stringify(a) === JSON.stringify(b);

export const TypographySettings = ({
  font,
  headlineFont,
  onChange,
  onHeadlineFontChange,
  inheritedFont,
  inheritedHeadlineFont,
  scope = 'standalone',
}: TypographySettingsProps) => {
  const { t } = useTranslation('settings');

  // Fetch Google Fonts from API (cached on backend for 24h)
  const { data: googleFonts = FALLBACK_FONTS, isLoading: fontsLoading } = useQuery({
    queryKey: ['google-fonts'],
    queryFn: async () => {
      const response = await apiClient.get('/api/branding/fonts');
      return response.data.data as Array<{ value: string; label: string }>;
    },
    staleTime: 1000 * 60 * 60, // 1 hour client-side cache
    gcTime: 1000 * 60 * 60 * 24, // Keep in cache for 24h
  });

  // Font weights - using translation keys
  const FONT_WEIGHTS = [
    { value: '300', label: t('settings_typography_weight_300') },
    { value: '400', label: t('settings_typography_weight_400') },
    { value: '500', label: t('settings_typography_weight_500') },
    { value: '600', label: t('settings_typography_weight_600') },
    { value: '700', label: t('settings_typography_weight_700') },
    { value: '800', label: t('settings_typography_weight_800') },
  ];
  const resetLabel =
    scope === 'portfolio-brand' ? t('settings_color_use_portfolio') : t('settings_color_use_default');
  // A font is "own" when set and not merely mirroring the inherited (resolved) value.
  const [bodyOwn, setBodyOwn] = useState<boolean>(() => !!font && !sameFont(font, inheritedFont));
  const [headlineOwn, setHeadlineOwn] = useState<boolean>(
    () => !!headlineFont && !sameFont(headlineFont, inheritedHeadlineFont)
  );
  const [selectedFont, setSelectedFont] = useState(font?.family || inheritedFont?.family || DEFAULT_FONT.family);
  const [selectedWeights, setSelectedWeights] = useState<string[]>(
    () => font?.weights ?? inheritedFont?.weights ?? getDefaultWeights()
  );
  const [includeItalic, setIncludeItalic] = useState<boolean>(
    () => font?.italic ?? inheritedFont?.italic ?? getDefaultItalic()
  );
  const [fontLoaded, setFontLoaded] = useState(false);
  const bodySignature = `${selectedFont}|${[...selectedWeights].sort().join(',')}|${includeItalic}`;
  const lastBodySignature = useRef(bodySignature);

  // Headline font state
  const [selectedHeadlineFont, setSelectedHeadlineFont] = useState(
    headlineFont?.family || inheritedHeadlineFont?.family || 'Wagner'
  );
  const [, setHeadlineFontLoaded] = useState(false);
  const [useCustomHeadlineFont, setUseCustomHeadlineFont] = useState(
    !!(headlineFont?.family || inheritedHeadlineFont?.family) &&
      (headlineFont?.family || inheritedHeadlineFont?.family) !== 'Wagner'
  );
  const headlineSignature = useCustomHeadlineFont && selectedHeadlineFont !== 'Wagner' ? selectedHeadlineFont : '';
  const lastHeadlineSignature = useRef(headlineSignature);

  // Generate Google Fonts URL
  const generateFontUrl = (
    family: string,
    weights: string[],
    italic: boolean
  ): string => {
    const formattedFamily = family.replace(/ /g, '+');

    if (italic) {
      // Format: ital,wght@0,400;0,500;1,400;1,500 (normal and italic for each weight)
      const specs: string[] = [];
      weights.forEach((w) => {
        specs.push(`0,${w}`); // normal
        specs.push(`1,${w}`); // italic
      });
      return `https://fonts.googleapis.com/css2?family=${formattedFamily}:ital,wght@${specs.join(';')}&display=swap`;
    } else {
      // Format: wght@400;500;600;700
      return `https://fonts.googleapis.com/css2?family=${formattedFamily}:wght@${weights.join(';')}&display=swap`;
    }
  };

  // Load font preview
  useEffect(() => {
    setFontLoaded(false);

    const fontUrl = generateFontUrl(selectedFont, selectedWeights, includeItalic);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = fontUrl;

    link.onload = () => {
      setFontLoaded(true);
    };

    link.onerror = () => {
      // Font failed to load, show preview anyway with fallback
      devLog.warn('Font failed to load:', fontUrl);
      setFontLoaded(true);
    };

    // Timeout fallback - if font doesn't load in 5 seconds, show preview anyway
    const timeout = setTimeout(() => {
      setFontLoaded(true);
    }, 5000);

    document.head.appendChild(link);

    return () => {
      clearTimeout(timeout);
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, [selectedFont, selectedWeights, includeItalic]);

  // Only a changed selection updates the draft. StrictMode replays mount effects,
  // so a one-time "initialized" flag would mark an untouched draft dirty.
  useEffect(() => {
    if (lastBodySignature.current === bodySignature) return;
    lastBodySignature.current = bodySignature;

    const fontUrl = generateFontUrl(selectedFont, selectedWeights, includeItalic);

    const newFont: BrandingFont = {
      family: selectedFont,
      url: fontUrl,
      weights: selectedWeights,
      italic: includeItalic,
    };

    setBodyOwn(true);
    onChange(newFont);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFont, selectedWeights, includeItalic, bodySignature]);

  // Load headline font preview
  useEffect(() => {
    if (!useCustomHeadlineFont || selectedHeadlineFont === 'Wagner') {
      setHeadlineFontLoaded(true);
      return;
    }

    setHeadlineFontLoaded(false);
    const fontUrl = generateFontUrl(selectedHeadlineFont, ['400', '700'], false);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = fontUrl;
    link.onload = () => setHeadlineFontLoaded(true);
    link.onerror = () => {
      devLog.warn('Headline font failed to load:', fontUrl);
      setHeadlineFontLoaded(true);
    };
    const timeout = setTimeout(() => setHeadlineFontLoaded(true), 5000);
    document.head.appendChild(link);

    return () => {
      clearTimeout(timeout);
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, [selectedHeadlineFont, useCustomHeadlineFont]);

  // Apply the same change check for the headline font's StrictMode replay.
  useEffect(() => {
    if (lastHeadlineSignature.current === headlineSignature) return;
    lastHeadlineSignature.current = headlineSignature;

    if (!useCustomHeadlineFont || selectedHeadlineFont === 'Wagner') {
      onHeadlineFontChange(undefined); // Use default Wagner
      return;
    }

    setHeadlineOwn(true);

    const fontUrl = generateFontUrl(selectedHeadlineFont, ['400', '700'], false);
    onHeadlineFontChange({
      family: selectedHeadlineFont,
      url: fontUrl,
      weights: ['400', '700'],
      italic: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHeadlineFont, useCustomHeadlineFont, headlineSignature]);

  const handleFontChange = (value: string) => {
    setSelectedFont(value);
  };

  const handleWeightToggle = (weight: string) => {
    setSelectedWeights((prev) => {
      if (prev.includes(weight)) {
        // Don't allow removing all weights
        if (prev.length === 1) return prev;
        return prev.filter((w) => w !== weight);
      } else {
        return [...prev, weight].sort();
      }
    });
  };

  // Clear the own body font: show the inherited (or default) font, draft = undefined (saved as null)
  const handleClearBody = () => {
    const base = inheritedFont;
    const family = base?.family ?? DEFAULT_FONT.family;
    const weights = base?.weights ? [...base.weights] : getDefaultWeights();
    const italic = base?.italic ?? getDefaultItalic();
    lastBodySignature.current = `${family}|${[...weights].sort().join(',')}|${italic}`;
    setSelectedFont(family);
    setSelectedWeights(weights);
    setIncludeItalic(italic);
    setBodyOwn(false);
    onChange(undefined);
  };

  const handleClearHeadline = () => {
    const family = inheritedHeadlineFont?.family ?? 'Wagner';
    const custom = family !== 'Wagner';
    lastHeadlineSignature.current = custom ? family : '';
    setSelectedHeadlineFont(family);
    setUseCustomHeadlineFont(custom);
    setHeadlineOwn(false);
    onHeadlineFontChange(undefined);
  };

  const handleReset = () => {
    if (bodyOwn) handleClearBody();
    if (headlineOwn) handleClearHeadline();
  };

  const hasAnyModification = bodyOwn || headlineOwn;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-base sm:text-xl font-semibold text-gray-900">{t('settings_typography_title')}</h2>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            {t('settings_typography_subtitle')}
          </p>
        </div>

        {hasAnyModification && (
          <Button variant="secondary" size="sm" onClick={handleReset} className="flex-shrink-0 flex items-center gap-1.5">
            <Icon name={ICONS.REFRESH} size="xs" ariaHidden />
            <span className="hidden sm:inline">{t('settings_typography_reset_button')}</span>
          </Button>
        )}
      </div>

      {/* Headline Font Selection */}
      <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 space-y-4 sm:space-y-6">
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
            {t('settings_typography_headline_title', { defaultValue: 'Headline Font' })}
          </h3>
          <p className="text-sm text-gray-600">
            {t('settings_typography_headline_subtitle', { defaultValue: 'Used for page headers, titles, and branding elements. Default is Wagner (self-hosted).' })}
          </p>
        </div>

        {headlineOwn && (
          <button
            type="button"
            onClick={handleClearHeadline}
            className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
            aria-label={t('settings_color_clear_aria', {
              label: t('settings_typography_headline_title', { defaultValue: 'Headline Font' }),
              action: resetLabel,
            })}
          >
            {resetLabel}
          </button>
        )}

        {/* Toggle: Use default vs custom */}
        <div className="flex items-center gap-3">
          <Checkbox
            checked={useCustomHeadlineFont}
            onCheckedChange={(checked) => {
              setUseCustomHeadlineFont(!!checked);
              if (!checked) {
                setSelectedHeadlineFont('Wagner');
              }
            }}
            inline
          />
          <div>
            <span className="text-sm font-medium text-gray-900">
              {t('settings_typography_headline_custom_toggle', { defaultValue: 'Use custom headline font' })}
            </span>
            <p className="text-xs text-gray-500">
              {t('settings_typography_headline_custom_hint', { defaultValue: 'Replace the default Wagner font with a Google Font' })}
            </p>
          </div>
        </div>

        {/* Custom Font Selector (shown when toggle is on) */}
        {useCustomHeadlineFont && (
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              {t('settings_typography_headline_family_label', { defaultValue: 'Headline Font Family' })}
            </label>
            <Combobox
              value={selectedHeadlineFont}
              onChange={(value: string) => setSelectedHeadlineFont(value)}
              options={googleFonts}
              placeholder={t('settings_typography_headline_family_placeholder', { defaultValue: 'Select a headline font...' })}
              loading={fontsLoading}
            />
          </div>
        )}

      </div>

      {/* Body Font Selection */}
      <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 space-y-4 sm:space-y-6">
        {bodyOwn && (
          <button
            type="button"
            onClick={handleClearBody}
            className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
            aria-label={t('settings_color_clear_aria', {
              label: t('settings_typography_family_label'),
              action: resetLabel,
            })}
          >
            {resetLabel}
          </button>
        )}

        {/* Font Family Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            {t('settings_typography_family_label')}
          </label>
          <Combobox
            value={selectedFont}
            onChange={handleFontChange}
            options={googleFonts}
            placeholder={t('settings_typography_family_placeholder')}
            loading={fontsLoading}
          />
          <p className="text-xs text-gray-500 mt-2">
            {t('settings_typography_family_hint')}
          </p>
        </div>

        {/* Font Weights */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            {t('settings_typography_weights_label')}
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
            {FONT_WEIGHTS.map((weight) => (
              <button
                key={weight.value}
                type="button"
                onClick={() => handleWeightToggle(weight.value)}
                className={`
                  px-2.5 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl border-2 text-xs sm:text-sm font-medium transition-all
                  ${
                    selectedWeights.includes(weight.value)
                      ? 'border-secondary-500 bg-secondary-50 text-secondary-900'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {weight.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {t('settings_typography_weights_hint')}
          </p>
        </div>

        {/* Italic Toggle */}
        <div className="flex items-center gap-3">
          <Checkbox
            checked={includeItalic}
            onCheckedChange={(checked) => setIncludeItalic(!!checked)}
            inline
          />
          <div>
            <span className="text-sm font-medium text-gray-900">
              {t('settings_typography_italic_label')}
            </span>
            <p className="text-xs text-gray-500">
              {t('settings_typography_italic_hint')}
            </p>
          </div>
        </div>
      </div>

      {/* Combined Preview */}
      <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
        <h3 className="text-sm font-medium text-gray-900 mb-4">{t('settings_typography_preview_title')}</h3>

        {fontLoaded ? (
          <div className="space-y-4">
            {/* Headings - use headline font */}
            <div className="space-y-2" style={{ fontFamily: useCustomHeadlineFont ? selectedHeadlineFont : 'Wagner' }}>
              <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 break-words">
                {t('settings_typography_preview_sample')}
              </h1>
              <h2 className="text-xl sm:text-3xl font-semibold text-gray-800 break-words">
                {t('settings_typography_preview_sample')}
              </h2>
              <h3 className="text-lg sm:text-2xl font-medium text-gray-700 break-words">
                {t('settings_typography_preview_sample')}
              </h3>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200" />

            {/* Body Text - use body font */}
            <div className="space-y-2" style={{ fontFamily: selectedFont }}>
              <p className="text-base text-gray-900">
                <strong>{t('settings_typography_weight_bold')}</strong> {t('settings_typography_preview_sample')}
              </p>
              <p className="text-base text-gray-700">
                {t('settings_typography_weight_regular')} {t('settings_typography_preview_sample')}
              </p>
              {includeItalic && (
                <p className="text-base text-gray-700 italic">
                  {t('settings_typography_weight_italic')} {t('settings_typography_preview_sample')}
                </p>
              )}
              <p className="text-sm text-gray-600">
                {t('settings_typography_weight_small')} {t('settings_typography_preview_sample')}
              </p>
            </div>

            {/* Numbers & Special Characters */}
            <div className="text-base text-gray-700" style={{ fontFamily: selectedFont }}>
              {/* eslint-disable-next-line i18next/no-literal-string */}
              <p>Numbers: 0123456789</p>
              {/* eslint-disable-next-line i18next/no-literal-string */}
              <p>Special: !@#$%^&*()_+-=[]{}|;':",./&lt;&gt;?</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <Icon name={ICONS.LOADER} size="lg" className="animate-spin mr-3" ariaHidden />
            <span>{t('settings_typography_loading')}</span>
          </div>
        )}
      </div>

      {/* Info Note */}
      <div className="bg-soft-mint border border-light-teal-gray rounded-xl p-3 sm:p-4">
        <div className="flex gap-2 sm:gap-3">
          <Icon name={ICONS.INFO} size="sm" color="text-dark-teal" ariaHidden className="flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs sm:text-sm font-medium text-dark-teal">{t('settings_typography_info_title')}</p>
            <ul className="text-xs sm:text-sm text-dark-teal mt-1.5 sm:mt-2 space-y-1 list-disc list-inside">
              <li>{t('settings_typography_tip_weights')}</li>
              <li>{t('settings_typography_tip_italic')}</li>
              <li>{t('settings_typography_tip_cache')}</li>
              <li>{t('settings_typography_tip_realtime')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
